/**
 * Who's logged in, and the way out (SPEC.md §2.2, §11 item 16).
 *
 * Sits next to `TokensCard`, `InvitesCard` and `SessionsCard` on Settings — this one's about
 * the account itself: its username, its password, logging out of it, and deleting it.
 *
 * Deletion has no undo, anywhere — `window.confirm` is the entire safety net, same as every
 * other destructive action in this app (revoking a token, deleting an area). The backend
 * (`routers/auth.py::delete_account`) does the actual work of cascading through every table
 * `user_id` was added to.
 */

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, api, type Me } from "../api";

export function AccountCard() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [savingUsername, setSavingUsername] = useState(false);

  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMe(await api.me());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function logOut() {
    setLoggingOut(true);
    try {
      // `api.logout()` fires `LOGGED_OUT_EVENT` itself on success — `AuthGate` drops back
      // to the login screen from that alone; nothing here needs to know how.
      await api.logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setLoggingOut(false);
    }
  }

  function startEditingUsername() {
    setUsernameDraft(me?.username ?? "");
    setUsernameError(null);
    setEditingUsername(true);
  }

  async function saveUsername(event: FormEvent) {
    event.preventDefault();
    setSavingUsername(true);
    setUsernameError(null);
    try {
      setMe(await api.updateUsername(usernameDraft.trim()));
      setEditingUsername(false);
    } catch (err) {
      setUsernameError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setSavingUsername(false);
    }
  }

  function toggleChangingPassword() {
    setChangingPassword((on) => !on);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setPasswordSaved(false);
  }

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation don't match.");
      return;
    }
    setSavingPassword(true);
    setPasswordError(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangingPassword(false);
      setPasswordSaved(true);
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setSavingPassword(false);
    }
  }

  async function deleteAccount() {
    if (
      !window.confirm(
        `Delete the account "${me?.username ?? ""}"?\n\n` +
          "This immediately and irrevocably deletes it - every area, project, task, note, " +
          "token and session, gone instantly. There is no undo and nothing to recover from.",
      )
    ) {
      return;
    }
    setDeletingAccount(true);
    setDeleteError(null);
    try {
      // `api.deleteAccount()` fires `LOGGED_OUT_EVENT` itself on success — `AuthGate` drops
      // back to the login screen from that alone; nothing here needs to know how.
      await api.deleteAccount();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setDeletingAccount(false);
    }
  }

  return (
    <section className="panel">
      <header>
        <h2>Account</h2>
      </header>

      {error && <p className="error">{error}</p>}
      {me === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <div style={{ display: "grid", gap: "0.6rem" }}>
          {editingUsername ? (
            <form onSubmit={saveUsername} style={{ display: "grid", gap: "0.5rem" }}>
              <input
                className="input"
                type="text"
                autoComplete="username"
                aria-label="Username"
                value={usernameDraft}
                onChange={(e) => setUsernameDraft(e.target.value)}
                autoFocus
              />
              {usernameError && <p className="error">{usernameError}</p>}
              <div className="row wrap">
                <button
                  className="btn primary small"
                  type="submit"
                  disabled={savingUsername || !usernameDraft.trim()}
                >
                  {savingUsername ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => setEditingUsername(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="row wrap" style={{ justifyContent: "space-between" }}>
              <p style={{ margin: 0 }}>
                {me.username}
                {me.is_admin && (
                  <span className="badge" style={{ marginLeft: "0.5rem" }}>
                    Owner
                  </span>
                )}
              </p>
              <div className="row wrap">
                <button className="btn ghost small" onClick={startEditingUsername}>
                  Edit username
                </button>
                <button
                  className="btn ghost small"
                  disabled={loggingOut}
                  onClick={() => void logOut()}
                >
                  {loggingOut ? "Logging out…" : "Log out"}
                </button>
              </div>
            </div>
          )}

          <div>
            <button type="button" className="btn ghost small" onClick={toggleChangingPassword}>
              {changingPassword ? "Never mind" : "Change password"}
            </button>
            {passwordSaved && !changingPassword && (
              <p className="small" style={{ margin: "0.4rem 0 0", color: "var(--good)" }}>
                Password changed.
              </p>
            )}
          </div>

          {changingPassword && (
            <form onSubmit={savePassword} style={{ display: "grid", gap: "0.5rem" }}>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                placeholder="Current password"
                aria-label="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                placeholder="New password"
                aria-label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm new password"
                aria-label="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {passwordError && <p className="error">{passwordError}</p>}
              <button
                className="btn primary small"
                type="submit"
                disabled={savingPassword || !currentPassword || !newPassword}
                style={{ justifySelf: "start" }}
              >
                {savingPassword ? "Saving…" : "Save new password"}
              </button>
            </form>
          )}

          <div>
            <button
              type="button"
              className="btn danger small"
              disabled={deletingAccount}
              onClick={() => void deleteAccount()}
            >
              {deletingAccount ? "Deleting…" : "Delete account"}
            </button>
            {deleteError && <p className="error">{deleteError}</p>}
          </div>
        </div>
      )}
    </section>
  );
}
